import { Component, DoCheck, inject, Input } from '@angular/core';
import { Params, Router, UrlTree } from '@angular/router';
import { ObjectUtils } from '../../utils/object.utils';
import { SearchFieldPipe } from '../pipes/search-field.pipe';
import { IEquipmentTypeFilter, IEquipmentTypeFilterOption } from '../../models/equipment-filter.model';
import { IEquipmentValues } from '../../models/catalog.models';

@Component({
  selector: 'app-equipment-type-filter',
  templateUrl: './equipment-type-filter.component.html',
  styleUrls: ['./equipment-type-filter.component.scss'],
  providers: [SearchFieldPipe],
  standalone: false,
})
export class EquipmentTypeFilterComponent implements DoCheck {
  isFilterOpened: boolean = false;
  filterSearchValue: string = '';
  filteredOptions: IEquipmentTypeFilterOption[] = [];

  @Input() filter!: IEquipmentTypeFilter;
  @Input() isEnhancedEquipmentTypeFilterEnabled: boolean = false;
  @Input() isPredefinedEquipmentFlowEnabled: boolean = false;

  private allOptionsSelected: boolean = false;
  private activeValues: Set<string> = new Set<string>();
  private lastValues: IEquipmentValues[] = [];
  private lastActiveValue: string[] = [];
  private lastSearchValue: string = '';
  private lastEnhancedToggle: boolean = false;

  private readonly router: Router = inject(Router);
  private readonly searchFieldPipe: SearchFieldPipe = inject(SearchFieldPipe);

  /**
   * Recomputes the rendered options only when something changed.
   */
  ngDoCheck(): void {
    const valuesChanged: boolean = this.filter?.values !== this.lastValues
      || this.filterSearchValue !== this.lastSearchValue
      || this.isEnhancedEquipmentTypeFilterEnabled !== this.lastEnhancedToggle;
    const activeValuesChanged: boolean = this.filter?.activeValue !== this.lastActiveValue;

    if (activeValuesChanged) {
      this.lastActiveValue = this.filter?.activeValue;
      this.activeValues = new Set<string>(this.filter?.activeValue ?? []);
    }

    if (valuesChanged) {
      this.lastValues = this.filter?.values;
      this.lastSearchValue = this.filterSearchValue;
      this.lastEnhancedToggle = this.isEnhancedEquipmentTypeFilterEnabled;
      this.buildFilteredOptions();
    } else if (activeValuesChanged) {
      this.refreshSelectedState();
    }
  }

  toggleFilterOptions(): void {
    this.isFilterOpened = true;

    const filterContainer: Element | null = document.querySelector('.equipment-filter');
    filterContainer?.classList.toggle('is-open');
  }

  selectOptionsOrClear(): void {
    const pageUrl: string = decodeURIComponent(this.router.url.split('?')[0]);

    if (this.areOptionsSelected()) {
      this.filter.activeValue = [];
      this.router.navigateByUrl(pageUrl).then();
      return;
    }

    const queryParams: {'rel-product-sysivk': string[], page: number} = {
      'rel-product-sysivk': this.filteredOptions.map(item => item.option['rel-product-sysivk']),
      page: 1,
    };

    const url: string = this.router.createUrlTree(
      [pageUrl],
      {queryParams},
    ).toString();

    this.router.navigateByUrl(url).then();
  }

  selectOption(option: IEquipmentValues): void {
    const pageUrl: string = decodeURIComponent(this.router.url.split('?')[0]);
    const queryParams: Params = this.createQueryParams('rel-product-sysivk', option['rel-product-sysivk']);

    const url: string = this.router.createUrlTree(
      [pageUrl],
      {queryParams},
    ).toString();

    this.router.navigateByUrl(url).then();
  }

  areOptionsSelected(): boolean {
    return this.allOptionsSelected;
  }

  isOptionSelected(option: IEquipmentValues): boolean {
    return this.activeValues.has(option['rel-product-sysivk']);
  }

  createQueryParams(name: string, value: string | number): Params {
    const urlTree: UrlTree = this.router.parseUrl(this.router.url);
    if (!urlTree.queryParams.hasOwnProperty(name)) {
      urlTree.queryParams[name] = value;
      urlTree.queryParams.page = 1;

      return urlTree.queryParams;
    }

    if (Array.isArray(urlTree.queryParams[name])) {
      if (urlTree.queryParams[name].find(param => param === value)) {
        urlTree.queryParams[name] = urlTree.queryParams[name].filter(param => param !== value);
      } else {
        urlTree.queryParams[name].push(value);
      }
    } else if (urlTree.queryParams[name] === value) {
      urlTree.queryParams = ObjectUtils.shallowFilterObject(urlTree.queryParams, name);
    } else {
      urlTree.queryParams[name] = [urlTree.queryParams[name], value];
    }

    urlTree.queryParams.page = 1;

    return urlTree.queryParams;
  }

  /**
   * Generates a unique key for a filter option, used for checkbox id/name bindings.
   * When enhanced equipment type filter is enabled, appends rel-product-sysivk
   * to ensure uniqueness when multiple equipment types share the same name.
   *
   * @param {IEquipmentValues} option
   * @returns {string}
   */
  getOptionKey(option: IEquipmentValues): string {
    if (this.isEnhancedEquipmentTypeFilterEnabled) {
      return `${this.filter.name}-${option.value}-${option['rel-product-sysivk']}`;
    }

    return `${this.filter.name}-${option.value}`;
  }

  /**
   * Applies the search term once and builds the options.
   *
   * @private
   */
  private buildFilteredOptions(): void {
    const values: IEquipmentValues[] = this.filter?.values ?? [];
    const matchingValues: IEquipmentValues[] = this.filterSearchValue
      ? this.searchFieldPipe.transform(values, this.filterSearchValue)
      : values;

    this.filteredOptions = matchingValues.map(option => ({
      option,
      key: this.getOptionKey(option),
      selected: this.isOptionSelected(option),
    }));

    this.updateAllOptionsSelectedState();
  }

  /**
   * Updates only the selected state without rerunning the search filter.
   *
   * @private
   */
  private refreshSelectedState(): void {
    this.filteredOptions.forEach(item => {
      item.selected = this.isOptionSelected(item.option);
    });

    this.updateAllOptionsSelectedState();
  }

  /**
   * Keeps allOptionsSelected state in sync with the rendered options.
   *
   * @private
   */
  private updateAllOptionsSelectedState(): void {
    this.allOptionsSelected = this.filteredOptions.every(item => item.selected);
  }
}
